if (!customElements.get("media-gallery")) {
  customElements.define(
    "media-gallery",
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia("(min-width: 750px)");
        this.setupViewerControls();

        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener("slideChanged", debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll("[data-target]").forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector("button")
            .addEventListener("click", this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes("thumbnail") && this.mql.matches) this.removeListSemantic();
      }

      setupViewerControls() {
        if (!this.elements.viewer) return;

        this.viewerControls = this.elements.viewer.querySelector(".product-gallery-controls");
        this.viewerPagination = this.elements.viewer.querySelector(".product-gallery-controls__pagination");

        if (!this.viewerControls || !this.viewerPagination) return;

        this.viewerControlsInitializedAt = Date.now();
        this.initialViewerVisibleSlideCount = null;
        this.refreshViewerControls();

        this.elements.viewer.addEventListener("slideChanged", this.onViewerControlSlideChanged.bind(this));
        this.elements.viewer.slider.addEventListener("scroll", () => this.queueViewerControlStateUpdate());

        this.viewerSlidesObserver = new MutationObserver(() => this.queueViewerControlsRefresh());
        this.viewerSlidesObserver.observe(this.elements.viewer.slider, {
          attributes: true,
          attributeFilter: ["class", "style"],
          childList: true,
          subtree: true,
        });
      }

      queueViewerControlsRefresh() {
        clearTimeout(this.viewerControlsRefreshTimeout);
        this.viewerControlsRefreshTimeout = setTimeout(() => this.refreshViewerControls(), 0);
      }

      queueViewerControlStateUpdate() {
        if (this.viewerControlStateFrame) cancelAnimationFrame(this.viewerControlStateFrame);
        this.viewerControlStateFrame = requestAnimationFrame(() => this.updateViewerControlState());
      }

      hideViewerControlsUntilReady() {
        clearTimeout(this.viewerControlsReadyTimeout);
        this.viewerControls.classList.remove("product-gallery-controls--ready");
      }

      queueViewerControlsReady(visibleSlides) {
        clearTimeout(this.viewerControlsReadyTimeout);
        this.viewerControlsReadyTimeout = setTimeout(() => {
          if (this.canShowViewerControls(visibleSlides)) {
            this.viewerControls?.classList.add("product-gallery-controls--ready");
          } else {
            this.queueViewerControlsReady(this.getVisibleViewerSlides());
          }
        }, 120);
      }

      canShowViewerControls(visibleSlides) {
        if (!visibleSlides.length) return false;
        if (visibleSlides.length <= 1) return true;
        if (this.initialViewerVisibleSlideCount === null) return false;
        if (visibleSlides.length !== this.initialViewerVisibleSlideCount) return true;
        if (visibleSlides.length < this.elements.viewer.sliderItems.length) return true;

        // Reduced from 1800ms to 300ms. The original 1.8s gate was designed to
        // prevent layout shifts during image loading, but in practice it caused
        // pagination dots to never appear when all slides are visible (e.g. when
        // no color-based filtering is active). 300ms is enough for initial layout.
        return Date.now() - this.viewerControlsInitializedAt > 300;
      }

      refreshViewerControls() {
        if (!this.elements.viewer || !this.viewerControls || !this.viewerPagination) return;
        this.hideViewerControlsUntilReady();
        this.elements.viewer.resetPages();
        const visibleSlides = this.getVisibleViewerSlides();
        if (this.initialViewerVisibleSlideCount === null) {
          this.initialViewerVisibleSlideCount = visibleSlides.length;
        }
        this.syncViewerSliderPages(visibleSlides);
        this.viewerControls.classList.toggle("product-gallery-controls--single-slide", visibleSlides.length <= 1);

        this.renderViewerPagination(visibleSlides);
        this.setActiveViewerPagination(Math.max((this.elements.viewer.currentPage || 1) - 1, 0));

        if (visibleSlides.length <= 1) {
          this.elements.viewer.prevButton?.setAttribute("disabled", "disabled");
          this.elements.viewer.nextButton?.setAttribute("disabled", "disabled");
        } else {
          this.elements.viewer.update();
          this.updateViewerControlState(visibleSlides);
        }

        this.queueViewerControlsReady(visibleSlides);
      }

      getVisibleViewerSlides() {
        return Array.from(this.elements.viewer.sliderItems || []).filter((slide) => {
          const styles = window.getComputedStyle(slide);
          const isActiveMedia = !this.activeViewerMediaIds || this.activeViewerMediaIds.has(slide.dataset.mediaId);

          return isActiveMedia && slide.clientWidth > 0 && styles.display !== "none" && styles.visibility !== "hidden";
        });
      }

      setActiveMediaSet(mediaIds) {
        if (!Array.isArray(mediaIds) || !mediaIds.length) return;

        this.activeViewerMediaIds = new Set(mediaIds);
        this.queueViewerControlsRefresh();
      }

      syncViewerSliderPages(visibleSlides) {
        this.elements.viewer.sliderItemsToShow = visibleSlides;

        if (visibleSlides.length < 2) {
          this.elements.viewer.sliderItemOffset = this.elements.viewer.slider.clientWidth || 1;
          this.elements.viewer.slidesPerPage = 1;
          this.elements.viewer.totalPages = visibleSlides.length;
          this.elements.viewer.currentPage = visibleSlides.length ? 1 : 0;
          return;
        }

        this.elements.viewer.sliderItemOffset = visibleSlides[1].offsetLeft - visibleSlides[0].offsetLeft;
        this.elements.viewer.slidesPerPage = Math.max(
          Math.floor(
            (this.elements.viewer.slider.clientWidth - visibleSlides[0].offsetLeft) /
              this.elements.viewer.sliderItemOffset,
          ),
          1,
        );
        this.elements.viewer.totalPages = visibleSlides.length - this.elements.viewer.slidesPerPage + 1;
      }

      getActiveViewerSlideIndex(visibleSlides = this.getVisibleViewerSlides()) {
        if (!visibleSlides.length) return 0;

        const scrollLeft = this.elements.viewer.slider.scrollLeft;
        return visibleSlides.reduce((closestIndex, slide, index) => {
          const currentDistance = Math.abs(slide.offsetLeft - scrollLeft);
          const closestDistance = Math.abs(visibleSlides[closestIndex].offsetLeft - scrollLeft);

          return currentDistance < closestDistance ? index : closestIndex;
        }, 0);
      }

      updateViewerControlState(visibleSlides = this.getVisibleViewerSlides()) {
        if (!this.elements.viewer?.prevButton || !this.elements.viewer?.nextButton) return;

        if (visibleSlides.length <= 1) {
          this.elements.viewer.prevButton.setAttribute("disabled", "disabled");
          this.elements.viewer.nextButton.setAttribute("disabled", "disabled");
          return;
        }

        const activeIndex = this.getActiveViewerSlideIndex(visibleSlides);
        const slidesPerPage = Math.max(this.elements.viewer.slidesPerPage || 1, 1);
        const lastScrollableIndex = Math.max(visibleSlides.length - slidesPerPage, 0);

        if (activeIndex <= 0) {
          this.elements.viewer.prevButton.setAttribute("disabled", "disabled");
        } else {
          this.elements.viewer.prevButton.removeAttribute("disabled");
        }

        if (activeIndex >= lastScrollableIndex) {
          this.elements.viewer.nextButton.setAttribute("disabled", "disabled");
        } else {
          this.elements.viewer.nextButton.removeAttribute("disabled");
        }
      }

      renderViewerPagination(visibleSlides) {
        this.viewerPagination.innerHTML = "";

        visibleSlides.forEach((slide, index) => {
          const button = document.createElement("button");
          button.type = "button";
          button.className = "slider-counter__link slider-counter__link--dots link";
          button.dataset.slideIndex = String(index);
          button.setAttribute("aria-label", `Slide ${index + 1}`);
          button.setAttribute("aria-controls", this.elements.viewer.slider.id);

          const dot = document.createElement("span");
          dot.className = "dot";
          button.appendChild(dot);
          button.addEventListener("click", this.onViewerPaginationClick.bind(this));

          this.viewerPagination.appendChild(button);
        });

        this.viewerPaginationButtons = Array.from(this.viewerPagination.querySelectorAll("[data-slide-index]"));
      }

      onViewerPaginationClick(event) {
        event.preventDefault();

        const slideIndex = Number(event.currentTarget.dataset.slideIndex);
        const slide = this.getVisibleViewerSlides()[slideIndex];

        if (!slide) return;

        this.elements.viewer.setSlidePosition(slide.offsetLeft);
      }

      onViewerControlSlideChanged(event) {
        const activeIndex = this.getActiveViewerSlideIndex();
        this.setActiveViewerPagination(activeIndex);
        this.updateViewerControlState();
      }

      setActiveViewerPagination(activeIndex) {
        if (!this.viewerPaginationButtons?.length) return;

        this.viewerPaginationButtons.forEach((button, index) => {
          const isActive = index === activeIndex;
          button.classList.toggle("slider-counter__link--active", isActive);

          if (isActive) {
            button.setAttribute("aria-current", "true");
          } else {
            button.removeAttribute("aria-current");
          }
        });
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`,
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        prepend = false; // Prevent reordering of media elements as per user request
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector("[data-media-id]");
        if (!activeMedia) {
          return;
        }

        // Pause the MutationObserver so DOM changes don't trigger
        // refreshViewerControls() which resets scroll position
        if (this.viewerSlidesObserver) this.viewerSlidesObserver.disconnect();

        this.elements.viewer.querySelectorAll("[data-media-id]").forEach((element) => {
          element.classList.remove("is-active");
        });
        activeMedia?.classList?.add("is-active");

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail &&
              activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        this.refreshViewerControls();

        // Scroll instantly to the active media.
        // CSS sets scroll-behavior:smooth on the slider, which forces a slow
        // animation even with JS behavior:'auto'. Override it temporarily.
        window.setTimeout(() => {
          const slider = activeMedia.parentElement;
          slider.style.scrollBehavior = 'auto';
          slider.scrollTo({ left: activeMedia.offsetLeft });

          // Restore smooth scrolling on next frame so user swipes still feel nice
          requestAnimationFrame(() => {
            slider.style.scrollBehavior = '';
          });

          this.queueViewerControlsRefresh();

          // Reconnect the MutationObserver now that scroll is set
          if (this.viewerSlidesObserver && this.elements.viewer?.slider) {
            this.viewerSlidesObserver.observe(this.elements.viewer.slider, {
              attributes: true,
              attributeFilter: ["class", "style"],
              childList: true,
              subtree: true,
            });
          }
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll("button")
          .forEach((element) => element.removeAttribute("aria-current"));
        thumbnail.querySelector("button").setAttribute("aria-current", true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({left: thumbnail.offsetLeft});
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector(".product__modal-opener--image img");
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute("aria-hidden", false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace("[index]", position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute("aria-hidden", true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector(".deferred-media");
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector("sticky-header");
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event("preventHeaderReveal"));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute("role", "presentation");
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute("role", "presentation"));
      }
    },
  );
}
